import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

interface LowStockProduct {
  id: number;
  sku: string;
  name: string;
  category_name: string;
  unit_size: string;
  qty_in_box: number;
  stock_quantity: number;
  stock_single: number;
  total_stock: number;
  reorder_level: number;
  unit_type: string;
  image_url: string;
}

const LowStock: React.FC = () => {
  const { hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<LowStockProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Check permissions - redirect if user doesn't have dashboard access
  useEffect(() => {
    if (!hasPermission('dashboard.read') && !hasRole(['super_admin', 'admin'])) {
      navigate('/products');
      return;
    }
  }, [hasPermission, hasRole, navigate]);

  useEffect(() => {
    // Only fetch if user has permission
    if (hasPermission('dashboard.read') || hasRole(['super_admin', 'admin'])) {
      fetchLowStockProducts();
    }
  }, [hasPermission, hasRole]);

  const fetchLowStockProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/products/low-stock');
      if (response.data.success) {
        const lowStockProducts = response.data.data;
        setProducts(lowStockProducts);
        
        // Extract unique categories
        const categorySet = new Set(lowStockProducts.map((p: LowStockProduct) => p.category_name));
        const uniqueCategories = Array.from(categorySet).filter(Boolean) as string[];
        setCategories(uniqueCategories);
      }
    } catch (error: any) {
      setError('Failed to fetch low stock products');
      console.error('Error fetching low stock products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const filteredProductIds = getFilteredProducts().map(product => product.id);
      setSelectedProducts(new Set(filteredProductIds));
    } else {
      setSelectedProducts(new Set());
    }
  };

  const handleSelectProduct = (productId: number) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const getFilteredProducts = () => {
    return products.filter(product => {
      const matchesSearch = !searchTerm || 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !categoryFilter || product.category_name === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  };

  const handleDownloadPDF = async () => {
    if (selectedProducts.size === 0) {
      setError('Please select at least one product to download.');
      return;
    }

    try {
      setDownloadingPDF(true);
      setError(null);

      const selectedProductIds = Array.from(selectedProducts);
      console.log('Downloading PDF for products:', selectedProductIds);

      const response = await api.post('/products/low-stock/pdf', {
        product_ids: selectedProductIds
      }, {
        responseType: 'blob',
        timeout: 30000,
      });

      console.log('PDF response received, size:', response.data.size);

      // Check if response is actually a PDF
      if (response.data.type !== 'application/pdf') {
        console.error('Response is not a PDF:', response.data.type);
        throw new Error('Invalid PDF response');
      }

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `low-stock-products-${new Date().toISOString().split('T')[0]}.pdf`;
      link.target = '_blank';

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup after a brief delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      console.log('PDF download initiated successfully');

    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      
      let errorMessage = 'Failed to download PDF. Please try again.';
      if (error.response?.status === 404) {
        errorMessage = 'Selected products not found.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to download this PDF.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while generating PDF. Please try again later.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        errorMessage = 'Network error or timeout. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const getStockStatusColor = (boxStock: number, reorderLevel: number) => {
    if (boxStock === 0) return 'error';
    if (boxStock <= 5) return 'error';
    if (boxStock <= reorderLevel) return 'warning';
    return 'default';
  };

  const getStockStatusText = (boxStock: number) => {
    if (boxStock === 0) return 'Out of Stock';
    if (boxStock <= 5) return 'Critical';
    return 'Low Stock';
  };

  const filteredProducts = getFilteredProducts();
  const paginatedProducts = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isAllSelected = filteredProducts.length > 0 && selectedProducts.size === filteredProducts.length;
  const isSomeSelected = selectedProducts.size > 0 && selectedProducts.size < filteredProducts.length;

  // Check permission before rendering
  if (!hasPermission('dashboard.read') && !hasRole(['super_admin', 'admin'])) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <WarningIcon color="warning" />
            <Typography variant="h4" component="h1">
              Low Stock Products
            </Typography>
          </Box>
          
          {(hasPermission('products.read') || hasRole(['super_admin', 'admin'])) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={downloadingPDF ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={handleDownloadPDF}
              disabled={downloadingPDF || selectedProducts.size === 0}
            >
              {downloadingPDF ? 'Generating...' : `Download PDF (${selectedProducts.size})`}
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Products with box stock levels of 15 or fewer units
        </Typography>

        {/* Total Products Count */}
        <Typography variant="body1" color="primary" sx={{ mb: 3, fontWeight: 'medium' }}>
          {filteredProducts.length === products.length 
            ? `Total: ${products.length} products`
            : `Showing: ${filteredProducts.length} of ${products.length} products`
          }
        </Typography>

        {/* Filters */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Products Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Image</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Unit Size</TableCell>
                <TableCell align="center">Box Stock</TableCell>
                <TableCell align="center">Single Stock</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.map((product) => (
                <TableRow
                  key={product.id}
                  hover
                  selected={selectedProducts.has(product.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      component="img"
                      src={getImageUrl(product.image_url)}
                      alt={product.name}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>{product.sku}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {product.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{product.category_name || 'Uncategorized'}</TableCell>
                  <TableCell>
                    {product.unit_size && product.qty_in_box 
                      ? `${product.unit_size} x ${product.qty_in_box}`
                      : product.unit_size || '-'
                    }
                  </TableCell>
                  <TableCell align="center">{product.stock_quantity}</TableCell>
                  <TableCell align="center">{product.stock_single}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={getStockStatusText(product.stock_quantity)}
                      color={getStockStatusColor(product.stock_quantity, product.reorder_level) as any}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredProducts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default LowStock;